import { KeyboardSensor, DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import {
	rectSortingStrategy, SortableContext, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import {
	CreateSurvey, LanguagesAbbr, Survey, SurveyQuestion, SurveyQuestionOption
} from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import TextInput from '../../../../../general-ui/text-input/text';
import { QuizQuestionComponent } from './quiz-question';

interface Props {
	addDefaultQuizOption: () => Promise<SurveyQuestionOption | undefined>;
	addQuestion: () => Promise<void>;
	handleDeleteQuestion: (question: SurveyQuestion) => void;
	Handlers: { [key: string]: any };
	language: LanguagesAbbr;
	questionErrors: string[];
	setWorkingQuestion: React.Dispatch<React.SetStateAction<SurveyQuestion | null>>;
	setWorkingQuestionTitle: ({ target: { value }, }: React.ChangeEvent<HTMLInputElement>) => void;
	setWorkingSurvey: React.Dispatch<React.SetStateAction<Survey | CreateSurvey | null>>;
	surveyErrors: string[];
	workingQuestion: SurveyQuestion | null;
	workingSurvey: Survey | CreateSurvey | null;
}

export function SurveyQuizForm({
	addDefaultQuizOption,
	addQuestion,
	handleDeleteQuestion,
	Handlers,
	language,
	questionErrors,
	setWorkingQuestion,
	setWorkingQuestionTitle,
	setWorkingSurvey,
	surveyErrors,
	workingQuestion,
	workingSurvey,
}: Props): JSX.Element | null {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	function setSuccessThreshold({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) return null;
			const updated = {
				...workingSurvey,
				passing_threshold: parseInt(value || '0'),
			};

			Handlers.validateSurvey(updated);

			return updated;
		});
	}

	if (!workingSurvey) return null;

	const isNewQuestion = !!workingQuestion && !workingSurvey.questions.some((q) => {
		return q.survey_question === workingQuestion?.survey_question;
	});

	return (
		<>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={Handlers.handleDragEnd}
				modifiers={[restrictToParentElement]}
				autoScroll={false}
			>
				<SortableContext
					items={workingSurvey.questions.map((question) => {
						return String(question.survey_question);
					})}
					strategy={rectSortingStrategy}
				>
					<div className="questions-list">
						{workingSurvey.questions.map((question, index) => {
							return (
								<QuizQuestionComponent
									addDefaultQuizOption={addDefaultQuizOption}
									handleDeleteQuestion={handleDeleteQuestion}
									Handlers={Handlers}
									id={String(question.survey_question)}
									isOpen={workingQuestion?.survey_question === question.survey_question}
									key={`survey-question-form-${index}`}
									language={language}
									question={question}
									questionErrors={questionErrors}
									sensors={sensors}
									setWorkingQuestion={setWorkingQuestion}
									setWorkingQuestionTitle={setWorkingQuestionTitle}
									setWorkingSurvey={setWorkingSurvey}
									workingQuestion={workingQuestion}
									workingSurvey={workingSurvey}
								/>
							);
						})}
						{isNewQuestion && (
							<QuizQuestionComponent
								addDefaultQuizOption={addDefaultQuizOption}
								handleDeleteQuestion={handleDeleteQuestion}
								Handlers={Handlers}
								id={String(workingQuestion.survey_question)}
								isOpen={workingQuestion?.survey_question === workingQuestion.survey_question}
								language={language}
								question={workingQuestion}
								questionErrors={questionErrors}
								sensors={sensors}
								setWorkingQuestion={setWorkingQuestion}
								setWorkingQuestionTitle={setWorkingQuestionTitle}
								setWorkingSurvey={setWorkingSurvey}
								workingQuestion={workingQuestion}
								workingSurvey={workingSurvey}
								isNewQuestion
							/>
						)}
					</div>
				</SortableContext>
			</DndContext>
			<button
				disabled={!!workingQuestion}
				className="clear no-style button-plain"
				onClick={addQuestion}
				style={{ marginBottom: '1em', color: '#5AE4FF', fontWeight: '600' }}
			>
				<Icon
					name={ICONS.ADD}
					size={12}
					color={workingQuestion ? COLORS.GRAY : COLORS.CYAN}
				/>
				<span> Add Question </span>
			</button>
			<span className="quiz-score-wrapper">
				<TextInput
					label={'Quiz Score '}
					onChange={setSuccessThreshold}
					numeric={ true }
					placeholder={'Type the number of questions necessary to pass the quiz'}
					value={workingSurvey.passing_threshold?.toString() ?? ''}
				/>
			</span>
			{surveyErrors.length > 0 && (
				<div className="quiz-error">
					{surveyErrors.map((error) => <div key={error}>{error}</div>)}
				</div>
			)}
		</>
	);
}
